// Renders the active screen based on `state.view`.

import { el, clear, beep, vibrate } from './dom.js';
import { renderKeypad } from './keypad.js';
import { state, notify } from '../state.js';
import { config } from '../config.js';
import { PWA_VERSION } from '../version.js';
import { api, HttpError } from '../api.js';
import { getEmployees, queuePush, queueLength } from '../storage.js';
import { enroll, logout } from '../services/session.js';
import { flush as flushQueue } from '../services/queue.js';

const $app = document.getElementById('app');

// Track the last rendered view so we only rebuild the body when it changes.
let _lastView = null;

export function render() {
  const viewChanged = state.view !== _lastView;
  _lastView = state.view;

  if (viewChanged) {
    // View transition: full rebuild.
    clear($app);
    $app.appendChild(renderHeader());
    $app.appendChild(renderViewBody());
    $app.appendChild(renderFooter());
    if (state.gpsDenied) $app.appendChild(renderGpsBanner());
    return;
  }

  // Same view — replace only header and footer in-place so the body
  // (keypad input, enroll form) is never disturbed by background notify() calls.
  const existingHeader = $app.querySelector('header');
  const existingFooter = $app.querySelector('footer');
  const existingBanner = $app.querySelector('.gps-banner');
  if (existingHeader) $app.replaceChild(renderHeader(), existingHeader);
  if (existingFooter) $app.replaceChild(renderFooter(), existingFooter);
  if (state.gpsDenied && !existingBanner) $app.appendChild(renderGpsBanner());
  else if (!state.gpsDenied && existingBanner) existingBanner.remove();

  // For the enroll screen, also update only the status message in-place
  // so the code input value is never lost.
  if (state.view === 'enroll') {
    const card = $app.querySelector('.enroll-card');
    if (card) {
      const existing = card.querySelector('.enroll-msg');
      if (state.message) {
        const fresh = el('div', { class: 'enroll-msg' }, state.message);
        if (existing) existing.replaceWith(fresh);
        else card.appendChild(fresh);
      } else if (existing) {
        existing.remove();
      }
    }
  }
}

// -----------------------------------------------------------------------------
// Header / footer
// -----------------------------------------------------------------------------

function renderHeader() {
  const left = el('div', { class: 'hd-left' }, [
    el('div', { class: 'hd-title' }, state.deviceMeta?.deviceName || 'Reloj Marcador'),
    state.pendingQueueCount > 0
      ? el('div', { class: 'hd-pending' }, `${state.pendingQueueCount} pendiente${state.pendingQueueCount === 1 ? '' : 's'}`)
      : null
  ]);

  const offsetSec = Math.round(state.serverOffsetMs / 1000);
  const serverNow = new Date(Date.now() + state.serverOffsetMs);

  const center = el('div', { class: 'hd-time' }, [
    el('div', { class: 'hd-time-row' }, [
      el('span', { class: 'hd-label' }, 'Local'),
      el('span', { class: 'hd-clock', id: 'hd-clock-local' }, formatClock(new Date()))
    ]),
    el('div', { class: 'hd-time-row' }, [
      el('span', { class: 'hd-label' }, 'Servidor'),
      el('span', { class: 'hd-clock', id: 'hd-clock-server' }, formatClock(serverNow)),
      state.serverTimeWarning
        ? el('span', { class: 'hd-warn', title: `Diferencia: ${offsetSec}s` }, '⚠')
        : null
    ])
  ]);

  const right = el('div', { class: 'hd-right' }, [
    state.online
      ? el('span', { class: 'hd-pill hd-pill-ok' }, '● En línea')
      : el('span', { class: 'hd-pill hd-pill-warn' }, '● Sin conexión'),
    !state.isInstalled
      ? el('button', { class: 'hd-install', onclick: triggerInstall }, 'Instalar')
      : null,
    state.deviceToken
      ? el('button', { class: 'hd-logout', onclick: confirmLogout }, 'Cerrar sesión')
      : null
  ]);

  return el('header', { class: 'hd' }, [left, center, right]);
}

function renderFooter() {
  return el('footer', { class: 'ft' }, [
    el('span', { class: 'ft-version' }, `v${PWA_VERSION}`),
    state.deviceMeta?.companyName
      ? el('span', { class: 'ft-company' }, state.deviceMeta.companyName)
      : null,
    state.lastHeartbeatAt
      ? el('span', { class: 'ft-hb' }, `Hearth Beat: ${formatClock(state.lastHeartbeatAt)}`)
      : null,
    state.deviceMeta?.deviceId
      ? el('span', { class: 'ft-id' }, `ID: ${shortId(state.deviceMeta.deviceId)}`)
      : null
  ]);
}

function renderGpsBanner() {
  return el('div', { class: 'gps-banner' },
    'Sin permiso de ubicación. Las marcas se enviarán sin GPS.');
}

// -----------------------------------------------------------------------------
// Views
// -----------------------------------------------------------------------------

function renderViewBody() {
  switch (state.view) {
    case 'enroll': return renderEnroll();
    case 'keypad': return renderKeypadView();
    case 'confirm': return renderConfirm();
    case 'feedback': return renderFeedback();
    case 'blocked': return renderBlocked();
    default: return el('div', { class: 'screen' }, 'Inicializando…');
  }
}

// ---- Enrollment ----

let _autoEnrollAttempted = false;

function renderEnroll() {
  // Auto-enroll when the page is opened via a QR URL containing ?t=TOKEN.
  if (!_autoEnrollAttempted) {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('t');
    if (t) {
      _autoEnrollAttempted = true;
      setTimeout(() => tryEnroll({ enrollmentToken: t }), 0);
    }
  }

  const codeInput = el('input', {
    type: 'text',
    inputmode: 'numeric',
    pattern: '[0-9]*',
    maxlength: 8,
    placeholder: '00000000',
    class: 'enroll-code'
  });

  const onManual = async () => {
    const code = (codeInput.value || '').trim();
    if (code.length !== 8) {
      state.message = 'El código debe tener 8 dígitos.';
      notify();
      return;
    }
    await tryEnroll({ shortCode: code });
  };

  const onScan = async () => {
    try {
      const { startScanner } = await import('../services/scanner.js');
      await startScanner((url) => {
        const t = parseTokenFromUrl(url);
        if (!t) {
          state.message = 'QR no válido.';
          notify();
          return;
        }
        tryEnroll({ enrollmentToken: t });
      });
    } catch (e) {
      state.message = 'No se pudo abrir la cámara: ' + e.message;
      notify();
    }
  };

  return el('main', { class: 'screen screen-enroll' }, [
    el('div', { class: 'enroll-card' }, [
      el('h1', {}, 'Matrícula del dispositivo'),
      el('p', { class: 'muted' }, 'Escanee el QR generado por el administrador o ingrese el código corto.'),

      el('div', { class: 'enroll-actions' }, [
        el('button', { class: 'btn-primary', onclick: onScan }, 'Escanear QR'),
        el('div', { class: 'enroll-or' }, '— o —'),
        el('div', { class: 'enroll-manual' }, [
          codeInput,
          el('button', { class: 'btn-primary', onclick: onManual }, 'Matricular')
        ])
      ]),

      state.message ? el('div', { class: 'enroll-msg' }, state.message) : null
    ])
  ]);
}

async function tryEnroll(args) {
  try {
    state.message = 'Matriculando…';
    notify();
    await enroll(args);
    state.message = null;
    state.view = 'keypad';
    // Remove the enrollment token from the URL so it is never re-used.
    history.replaceState(null, '', '/');
    notify();
    // Kick off background services.
    const { startHeartbeat } = await import('../services/heartbeat.js');
    const { startSync } = await import('../services/sync.js');
    const { startQueueWorker } = await import('../services/queue.js');
    await Promise.all([startHeartbeat(), startSync(), startQueueWorker()]);
  } catch (e) {
    state.message = e.message || 'Error de matrícula.';
    notify();
  }
}

function parseTokenFromUrl(text) {
  if (!text) return null;
  // Accept either a full URL (?t=...) or just the raw token.
  try {
    const u = new URL(text);
    return u.searchParams.get('t');
  } catch {
    return text.length > 16 ? text : null;
  }
}

// ---- Keypad ----

function renderKeypadView() {
  return el('main', { class: 'screen screen-keypad' }, [
    renderKeypad(onSubmitIdentification)
  ]);
}

async function onSubmitIdentification(identification) {
  const normalize = (s) => s.replace(/[\s\-]/g, '');
  const local = await getEmployees();
  const found = local.find((e) => normalize(e.identification) === normalize(identification));
  if (found) {
    state.candidate = found;
    state.view = 'confirm';
    notify();
    return;
  }

  // Not in cache — ask server (only if online).
  if (!navigator.onLine) {
    state.feedback = { kind: 'error', text: `No se encontró al colaborador ${identification}.` };
    state.view = 'feedback';
    notify();
    autoReturnToKeypad();
    return;
  }
  try {
    const res = await api.lookup(identification);
    if (!res?.success || !res.result) {
      state.feedback = { kind: 'error', text: res?.message || `No se encontró al colaborador ${identification}.` };
      state.view = 'feedback';
      notify();
      autoReturnToKeypad();
      return;
    }
    state.candidate = res.result;
    state.view = 'confirm';
    notify();
  } catch (err) {
    if (err instanceof HttpError && err.status === 403) {
      // Device was blocked while the user was interacting.
      state.view = 'blocked';
      state.message = 'Este dispositivo ha sido bloqueado por el administrador.';
      notify();
      return;
    }
    if (err instanceof HttpError && err.status === 401) {
      await logout();
      return;
    }
    state.feedback = { kind: 'error', text: 'Sin conexión. No se pudo validar.' };
    state.view = 'feedback';
    notify();
    autoReturnToKeypad();
  }
}

// ---- Confirm ----

function renderConfirm() {
  const c = state.candidate;
  const onYes = async () => {
    try {
      const item = {
        clientUuid: cryptoUuid(),
        employeeIdentification: c.identification,
        deviceReportedAt: new Date().toISOString(),
        lat: state.lastGps?.lat ?? null,
        lng: state.lastGps?.lng ?? null
      };
      await queuePush(item);
      state.pendingQueueCount = await queueLength();
      state.feedback = { kind: 'success', text: `¡Marca registrada para ${c.fullName}!` };
      state.view = 'feedback';
      state.candidate = null;
      state.inputDigits = '';
      notify();
      beep();
      vibrate(60);
      autoReturnToKeypad();
      // Try a fast flush — best-effort, non-blocking.
      flushQueue().catch(() => {});
    } catch (err) {
      console.error('Error al registrar marca', err);
      state.feedback = { kind: 'error', text: 'Error al guardar la marca. Intente de nuevo.' };
      state.view = 'feedback';
      notify();
      autoReturnToKeypad();
    }
  };
  const onNo = () => {
    state.candidate = null;
    state.inputDigits = '';
    state.view = 'keypad';
    notify();
  };

  return el('main', { class: 'screen screen-confirm' }, [
    el('div', { class: 'confirm-card' }, [
      el('div', { class: 'muted' }, 'Confirme su identidad'),
      el('div', { class: 'confirm-name' }, c.fullName),
      el('div', { class: 'confirm-id' }, c.identification),
      el('div', { class: 'confirm-actions' }, [
        el('button', { class: 'btn-danger big', onclick: onNo }, 'NO soy yo'),
        el('button', { class: 'btn-success big', onclick: onYes }, 'SÍ, registrar marca')
      ])
    ])
  ]);
}

// ---- Feedback ----

function renderFeedback() {
  const fb = state.feedback || { kind: 'success', text: '' };
  return el('main', { class: 'screen screen-feedback' }, [
    el('div', { class: 'feedback-card ' + (fb.kind === 'success' ? 'is-ok' : 'is-err') }, [
      el('div', { class: 'feedback-icon' }, fb.kind === 'success' ? '✓' : '✕'),
      el('div', { class: 'feedback-text' }, fb.text)
    ])
  ]);
}

let returnTimer = null;
function autoReturnToKeypad() {
  if (returnTimer) clearTimeout(returnTimer);
  returnTimer = setTimeout(() => {
    state.feedback = null;
    state.view = state.deviceToken ? 'keypad' : 'enroll';
    state.inputDigits = '';
    notify();
  }, 2200);
}

// ---- Blocked ----

function renderBlocked() {
  return el('main', { class: 'screen screen-blocked' }, [
    el('div', { class: 'blocked-card' }, [
      el('div', { class: 'blocked-icon' }, '⛔'),
      el('h1', {}, 'Dispositivo bloqueado'),
      el('p', {}, state.message || 'Este dispositivo ha sido deshabilitado por el administrador.'),
      el('button', { class: 'btn-secondary', onclick: confirmLogout }, 'Cerrar sesión')
    ])
  ]);
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function confirmLogout() {
  if (confirm('¿Cerrar sesión y borrar la configuración del dispositivo?')) {
    logout();
  }
}

async function triggerInstall() {
  if (state.installPrompt) {
    const prompt = state.installPrompt;
    state.installPrompt = null;
    notify();
    await prompt.prompt();
    await prompt.userChoice;
    return;
  }
  // Fallback for iOS Safari and other browsers that don't support beforeinstallprompt.
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const msg = isIos
    ? 'Para instalar: toca el botón Compartir (⎙) y luego "Agregar a pantalla de inicio".'
    : 'Para instalar: abre el menú del navegador y selecciona "Instalar app" o "Agregar a pantalla de inicio".';
  alert(msg);
}

function formatClock(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function shortId(id) {
  return (id || '').split('-')[0];
}

function cryptoUuid() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
}

// Ticking clock: update only the clock DOM elements in-place to avoid a full re-render.
setInterval(() => {
  if (document.hidden) return;
  const localEl = document.getElementById('hd-clock-local');
  const serverEl = document.getElementById('hd-clock-server');
  if (localEl) localEl.textContent = formatClock(new Date());
  if (serverEl) serverEl.textContent = formatClock(new Date(Date.now() + state.serverOffsetMs));
}, 1000);
